// The short "about your family" quiz shown at redemption.
// Ids, option ids and value formats are IDENTICAL to the marketing quiz
// (MarketingFunnels src/config/quiz.ts) so the app's Clerk webhook, child
// creation (baby-age) and plan generation read the answers unchanged.
// Multi-select answers are stored comma-joined, as in the funnel.

export interface QuizOption {
  id: string;
  label: string;
  emoji?: string;
  /** Branch to a different next step when this option is picked. */
  nextId?: string;
}

interface BaseStep {
  id: string;
  title: string;
  subtitle?: string;
  nextId: string | null;
}

export interface SingleStep extends BaseStep {
  type: 'single';
  options: QuizOption[];
}

export interface MultiStep extends BaseStep {
  type: 'multi';
  options: QuizOption[];
}

export interface DateStep extends BaseStep {
  type: 'date';
}

export interface TextareaStep extends BaseStep {
  type: 'textarea';
  placeholder: string;
  max: number;
}

export type QuizStep = SingleStep | MultiStep | DateStep | TextareaStep;

const AGE_OPTIONS: QuizOption[] = [
  { id: '0-3-months', label: '0 to 3 months old' },
  { id: '4-6-months', label: '4 to 6 months old' },
  { id: '7-11-months', label: '7 to 11 months old' },
  { id: '1-year', label: '1 year old' },
  { id: '2-years', label: '2 years old' },
  { id: '3-6-years', label: '3 to 6 years old' },
  { id: 'over-6-years', label: 'Over 6 years old' },
];

export const REDEEM_STEPS: QuizStep[] = [
  {
    id: 'parent-age',
    type: 'single',
    title: 'How old are you?',
    subtitle: 'So the plan fits your life, not just the baby’s.',
    nextId: 'children-count',
    options: [
      { id: '18-24', label: '18 to 24' },
      { id: '25-34', label: '25 to 34' },
      { id: '35-44', label: '35 to 44' },
      { id: '45-plus', label: '45+' },
    ],
  },
  {
    id: 'children-count',
    type: 'single',
    title: 'How many children do you have?',
    nextId: 'multiple-children-ages',
    options: [
      { id: '1', label: '1 child', nextId: 'single-child-age' },
      { id: '2', label: '2 children' },
      { id: '3', label: '3 children' },
      { id: '4-plus', label: '4 or more children' },
    ],
  },
  { id: 'single-child-age', type: 'single', title: 'How old is your child?', nextId: 'parenting-status', options: AGE_OPTIONS },
  {
    id: 'multiple-children-ages',
    type: 'multi',
    title: 'How old are your children?',
    subtitle: 'Select all the ages that apply',
    nextId: 'parenting-status',
    options: AGE_OPTIONS,
  },
  {
    id: 'parenting-status',
    type: 'single',
    title: "What's your current parenting status?",
    nextId: 'daily-rhythm',
    options: [
      { id: 'single-parent', label: 'Single parent' },
      { id: 'married-partner', label: 'Married or partnered' },
      { id: 'separated-sharing', label: 'Separated but sharing parenting' },
      { id: 'extended-family', label: 'Raising children with extended family' },
      { id: 'full-time-care', label: 'Full-time help' },
    ],
  },
  {
    id: 'daily-rhythm',
    type: 'single',
    title: "What's your typical weekday routine?",
    subtitle: 'Choose your main setup',
    nextId: 'sleep-feeling',
    options: [
      { id: 'home-full-time', label: 'Home with baby full time' },
      { id: 'home-part-time', label: 'Home with baby part time' },
      { id: 'working-full-time', label: 'Working full-time out of home' },
      { id: 'working-part-time', label: 'Hybrid (some office, some home)' },
      { id: 'on-leave', label: 'On leave / In transition' },
    ],
  },
  {
    id: 'sleep-feeling',
    type: 'single',
    title: 'How do you feel when you wake up?',
    nextId: 'overwhelmed',
    options: [
      { id: 'exhausted', label: 'Exhausted', emoji: '🪫' },
      { id: 'drained', label: 'Drained', emoji: '🔋' },
      { id: 'okay', label: 'Okay', emoji: '🙂' },
      { id: 'energized', label: 'Energized', emoji: '⚡' },
    ],
  },
  {
    id: 'overwhelmed',
    type: 'single',
    title: 'How often do you feel overwhelmed by the amount of work you have to do?',
    nextId: 'sleep-goals',
    options: [
      { id: 'often', label: 'Often' },
      { id: 'sometimes', label: 'Sometimes' },
      { id: 'almost-never', label: 'Almost never' },
    ],
  },
  {
    id: 'sleep-goals',
    type: 'multi',
    title: "Which areas would you like to focus on?",
    subtitle: 'Select all that apply',
    nextId: 'sleep-challenges',
    options: [
      { id: 'predictable', label: 'Get on a consistent schedule', emoji: '📅' },
      { id: 'reduce-wakeups', label: 'Fewer night wakeups', emoji: '🌙' },
      { id: 'improve-naps', label: 'Better naps', emoji: '☀️' },
      { id: 'end-battles', label: 'Make bedtime easier', emoji: '🛏️' },
      { id: 'stop-early-wakes', label: 'Stop early morning wakeups', emoji: '🌅' },
    ],
  },
  {
    id: 'sleep-challenges',
    type: 'multi',
    title: 'What will better sleep help you do?',
    subtitle: 'Select all that apply',
    nextId: 'baby-wakeup-time',
    options: [
      { id: 'get-myself-back', label: 'Get myself back' },
      { id: 'be-there-for-my-family', label: 'Be there for my family' },
      { id: 'recharge-myself', label: 'Recharge myself' },
      { id: 'keep-my-cool', label: 'Keep my cool' },
      { id: 'feel-in-control', label: 'Feel in control' },
    ],
  },
  {
    id: 'baby-wakeup-time',
    type: 'single',
    title: 'Most mornings, when is your little one up for the day?',
    nextId: 'baby-how-many-naps',
    options: [
      { id: 'before-5-am', label: 'Before 5 AM' },
      { id: 'between-5-am-and-6-am', label: '5 to 6 AM' },
      { id: 'between-6-am-and-7-am', label: '6 to 7 AM' },
      { id: 'between-7-am-and-8-am', label: '7 to 8 AM' },
      { id: 'after-8-am', label: 'After 8 AM' },
      { id: 'not-sure', label: 'It changes day to day' },
    ],
  },
  {
    id: 'baby-how-many-naps',
    type: 'single',
    title: 'How many naps does your little one take on most days?',
    nextId: 'baby-nap-length',
    options: [
      { id: '0-naps', label: '0 naps' },
      { id: '1-naps', label: '1 nap' },
      { id: '2-naps', label: '2 naps' },
      { id: '3-naps', label: '3 naps' },
      { id: '4-plus-naps', label: '4+ naps' },
      { id: 'not-sure', label: 'It changes day to day' },
    ],
  },
  {
    id: 'baby-nap-length',
    type: 'single',
    title: 'A typical nap lasts...',
    nextId: 'baby-bedtime',
    options: [
      { id: 'less-than-30-mins', label: 'Under 30 mins (catnaps)' },
      { id: '30-to-59-mins', label: '30 to 60 mins' },
      { id: '60-to-89-mins', label: '1 to 1.5 hours' },
      { id: 'over-90-mins', label: 'Over 1.5 hours' },
      { id: 'not-sure', label: 'It changes day to day' },
    ],
  },
  {
    id: 'baby-bedtime',
    type: 'single',
    title: 'Most nights, what time do they fall asleep?',
    nextId: 'baby-fall-asleep-time',
    options: [
      { id: 'before-6-pm', label: 'Before 6 PM' },
      { id: '6-pm-to-7-pm', label: '6 to 7 PM' },
      { id: '7-pm-to-8-pm', label: '7 to 8 PM' },
      { id: '8-pm-to-9-pm', label: '8 to 9 PM' },
      { id: 'after-9-pm', label: 'After 9 PM' },
      { id: 'not-sure', label: 'It changes night to night' },
    ],
  },
  {
    id: 'baby-fall-asleep-time',
    type: 'single',
    title: 'How long does it take them to fall asleep at night?',
    nextId: 'baby-night-wakeups',
    options: [
      { id: 'over-60-mins', label: 'Over 60 mins' },
      { id: '30-to-59-mins', label: '30 to 60 mins' },
      { id: '20-to-29-mins', label: '20 to 30 mins' },
      { id: '10-to-19-mins', label: '10 to 20 mins' },
      { id: 'less-than-10-mins', label: 'Under 10 mins' },
      { id: 'not-sure', label: 'It changes night to night' },
    ],
  },
  {
    id: 'baby-night-wakeups',
    type: 'single',
    title: 'How many times do they typically wake up at night?',
    nextId: 'baby-night-response',
    options: [
      { id: 'over-3-times', label: '3 or more times' },
      { id: '2-times', label: '2 times' },
      { id: '1-time', label: '1 time' },
      { id: 'not-sure', label: 'It changes night to night' },
      { id: 'doesnt-wake-up', label: "Doesn't wake up at all" },
    ],
  },
  {
    id: 'baby-night-response',
    type: 'multi',
    title: 'When they wake, what helps them fall back asleep?',
    subtitle: 'Select all that apply',
    nextId: 'baby-settle-methods',
    options: [
      { id: 'feed-them', label: 'Feed them' },
      { id: 'rock-or-hold', label: 'Rock or hold them' },
      { id: 'sit-or-shush', label: 'Sit or shush them' },
      { id: 'resettle-on-their-own', label: 'They resettle on their own' },
      { id: 'it-varies', label: 'It changes night to night' },
    ],
  },
  {
    id: 'baby-settle-methods',
    type: 'multi',
    title: 'Which of these do you use to get them to sleep?',
    subtitle: "Select whatever's working for you right now",
    nextId: 'baby-age',
    options: [
      { id: 'rocking', label: 'Rocking' },
      { id: 'feeding', label: 'Feeding to sleep' },
      { id: 'sitting-next-to-them', label: 'Holding / sitting beside' },
      { id: 'singing', label: 'Singing / shushing' },
      { id: 'falls-asleep-alone', label: 'They settle on their own' },
      { id: 'it-depends', label: 'It depends' },
    ],
  },
  {
    id: 'baby-age',
    type: 'date',
    title: "What's your little one's birth date?",
    subtitle: 'We use this to build the plan around their age.',
    nextId: 'additional-context',
  },
  {
    id: 'additional-context',
    type: 'textarea',
    title: 'Almost done! Anything else we should know?',
    subtitle: 'Co-sleeping, reflux, contact naps: our experts tailor your plan around your reality.',
    nextId: null,
    placeholder: 'E.g., we room share, baby has reflux, tried everything and nothing sticks…',
    max: 1000,
  },
];

export function getStep(id: string): QuizStep | undefined {
  return REDEEM_STEPS.find((s) => s.id === id);
}

export const FIRST_STEP_ID = 'parent-age';

/** Linear position for the progress bar (branches share a slot). */
export function progressFor(stepId: string): number {
  const order = REDEEM_STEPS.filter((s) => s.id !== 'multiple-children-ages').map((s) => s.id);
  const idx = order.indexOf(stepId === 'multiple-children-ages' ? 'single-child-age' : stepId);
  // +2 for the email and name screens that follow the quiz.
  return Math.round(((idx + 1) / (order.length + 2)) * 100);
}

export function nextStepId(step: QuizStep, answer: string): string | null {
  if (step.type === 'single') {
    const opt = step.options.find((o) => o.id === answer);
    if (opt?.nextId) return opt.nextId;
  }
  if (step.type === 'multi') {
    const picked = answer.split(',');
    const opt = step.options.find((o) => picked.includes(o.id) && o.nextId);
    if (opt?.nextId) return opt.nextId;
  }
  return step.nextId;
}

/**
 * Answers to submit. Drops the inactive child-age branch so a parent who
 * went back and changed the child count does not leave a stale key behind
 * (the app reads both `single-child-age` and `multiple-children-ages`).
 */
export function finalizeAnswers(answers: Record<string, string>): Record<string, string> {
  const out = { ...answers };
  if (out['children-count'] === '1') delete out['multiple-children-ages'];
  else delete out['single-child-age'];
  return out;
}
